import { ChangeDetectionStrategy, Component, Input, TrackByFunction } from '@angular/core';
import {
	ButtonComponent,
	DatalayerService,
	FeatureService,
	LocalStorageService,
	MarkdownService,
	ModalEvent,
	ModalOverlayService,
	ModalRoutingService,
	NotificationType,
	SentenceCasePipe,
	ShopperService,
	StorageKeys,
	SvgIconComponent,
} from '@woolworthsnz/styleguide';
import { ContextResponse, IngredientProduct } from '@woolworthsnz/trader-api';
import { RECIPE_ADD_INGREDIENTS_MODAL_PATH } from '../add-ingredients-modal/add-ingredients-modal.component';
import { GroupedIngredients, orderAndGroupIngredients } from '../helpers/ingredients-mapper';
import { ContributorComponent } from '../contributor/contributor.component';
import { NgFor, NgIf } from '@angular/common';

@Component({
	selector: 'recipe-method',
	templateUrl: './method.component.html',
	styleUrls: ['./method.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [NgFor, NgIf, ButtonComponent, SvgIconComponent, ContributorComponent, SentenceCasePipe],
})
export class MethodComponent {
	@Input() id: number;
	@Input() title: string;
	@Input() servings: string;
	@Input() ingredients: IngredientProduct[];
	@Input() contributorName: string;
	@Input() contributorTown: string;
	@Input() contributorLink: string;
	@Input() modalOutletName: string;
	@Input() enableAddToTrolley: boolean;

	markdownData: string;

	constructor(
		private markdownService: MarkdownService,
		private modalRoutingService: ModalRoutingService,
		private localStorageService: LocalStorageService,
		private featureService: FeatureService,
		private datalayerService: DatalayerService,
		private modalOverlayService: ModalOverlayService,
		private shopperService: ShopperService
	) {}

	get orderedIngredients(): GroupedIngredients[] {
		return orderAndGroupIngredients(this.ingredients);
	}

	@Input() set method(value: string) {
		this.markdownData = this.markdownService.parse(value);
	}

	trackByGroupsFn: TrackByFunction<GroupedIngredients> = (_, i) => i.heading;
	trackByIngredientsFn: TrackByFunction<IngredientProduct> = (_, i) => i.id;

	public addItemsToTrolley(): void {
		if (this.shouldShowLiquorLegislationModal()) {
			const confirmAction = (): void => {
				const scvid = this.shopperService.state?.shopperScvId || 'confirmed';
				this.localStorageService.setItem(StorageKeys.ageRestrictedProductAcknowledged, scvid);
				this.addItemsToTrolley();
			};
			this.modalOverlayService.openLiquorLegislationModal(confirmAction);
		} else {
			if (!this.shopperService.isLoggedInUser) {
				this.modalOverlayService.open({
					eventType: ModalEvent.addToTrolley,
				});
				return;
			}

			if (!this.shopperService.isShopper) {
				this.modalOverlayService.open({
					eventType: ModalEvent.notAuthorised,
				});
				return;
			}

			this.datalayerService.trackRecipeEvent(NotificationType.Button, 'RDP_add_to_cart', this.id);

			this.modalRoutingService.openWithOptions(
				this.modalOutletName,
				{
					state: { title: this.title, ingredients: this.orderedIngredients, servings: this.servings },
				},
				RECIPE_ADD_INGREDIENTS_MODAL_PATH,
				this.id.toString()
			);
		}
	}

	shouldShowLiquorLegislationModal(): boolean {
		const IsAgeRestrictedPopupEnabled = this.featureService.isFeatureEnabled(
			ContextResponse.EnabledFeaturesEnum.IsAgeRestrictedPopupEnabled
		);
		const hasLiquorItems = this.ingredients.some((ingredient) => ingredient.item?.isAgeRestricted);
		const cacheAgeConfirmed = this.localStorageService.getItem(StorageKeys.ageRestrictedProductAcknowledged);
		const ageConfirmed = this.shopperService.state?.shopperScvId
			? cacheAgeConfirmed === this.shopperService.state?.shopperScvId
			: cacheAgeConfirmed === 'confirmed';
		return IsAgeRestrictedPopupEnabled && hasLiquorItems && !ageConfirmed;
	}
}
